import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const initials = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function Team() {
  const [team, setTeam] = useState([]);
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  if (!team.length && !settings.group_photo) return null;

  return (
    <section id="team" data-testid="team" className="relative z-10 py-24 md:py-32 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="max-w-2xl mb-16">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-primary mb-5">/ The People</p>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
            The team behind<br /><span className="text-amber">the kingdom</span>
          </h2>
          <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed">
            Seasoned operators across investment strategy, operations and advisory — the people
            engineering disciplined cashflow for every client.
          </p>
        </div>

        {settings.group_photo && (
          <motion.figure
            data-testid="team-group-photo"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="mb-12 border border-border overflow-hidden"
          >
            <div className="aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
              <img src={settings.group_photo} alt={settings.group_photo_caption || "The team"} className="w-full h-full object-cover" />
            </div>
            {settings.group_photo_caption && (
              <figcaption className="bg-card border-t border-border px-6 py-4 font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                {settings.group_photo_caption}
              </figcaption>
            )}
          </motion.figure>
        )}

        {team.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {team.map((m, i) => (
            <motion.div
              key={m.id}
              data-testid={`team-member-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.55 }}
              className="group bg-background p-8 hover:bg-card transition-colors"
            >
              <div className="flex items-center gap-4 mb-6">
                {m.photo ? (
                  <img src={m.photo} alt={m.name} className="w-16 h-16 object-cover border border-border" />
                ) : (
                  <div className="w-16 h-16 border border-border flex items-center justify-center bg-secondary group-hover:border-primary transition-colors">
                    <span className="font-display font-black text-lg text-primary">{initials(m.name)}</span>
                  </div>
                )}
                <div>
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-xl leading-tight">{m.name}</h3>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.15em] text-amber mt-1.5">{m.role}</p>
                </div>
              </div>
              {m.bio && <p className="text-muted-foreground text-sm leading-relaxed">{m.bio}</p>}
            </motion.div>
          ))}
        </div>
        )}
      </div>
    </section>
  );
}
